import { DownloadOutlined } from "@ant-design/icons";
import {
  Button, Form, Input, Modal, Popconfirm, Select, Space, message,
} from "antd";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import DateObject from "react-date-object";
import gregorian from "react-date-object/calendars/gregorian";
import gregorian_en from "react-date-object/locales/gregorian_en";
import jalali from "react-date-object/calendars/jalali";
import persian_fa from "react-date-object/locales/persian_fa";
import DatePicker from "react-multi-date-picker";
import api from "../../../api/api";
import DataTable from "../../../Components/DataTable/DataTable";
import CheckPermission from "../../../Util/CheckPermission";
import { getLeadOwnerLabel, getLeadOwnerValues, leadOwnerOptions } from "../../../Util/GetLeadOwnerLabel";
import exportDate from "./exportDate";

const renderExportDate = (value) => (
  <span dir="ltr">{exportDate(value)?.format("YYYY/MM/DD") || "—"}</span>
);

const ExportDatePicker = ({ value, onChange }) => (
  <DatePicker
    value={exportDate(value)}
    onChange={(date) => onChange(date
      ? new DateObject(date).convert(gregorian, gregorian_en).format("YYYY-MM-DD")
      : null
    )}
    calendar={jalali}
    locale={persian_fa}
    format="YYYY/MM/DD"
    editable={false}
    render={(displayValue, openCalendar) => (
      <Input
        value={displayValue}
        onClick={openCalendar}
        placeholder="اختیاری"
        allowClear
        readOnly
        onChange={(event) => { if (!event.target.value) onChange(null); }}
      />
    )}
  />
);

const LeadExports = ({ canList, canView, canCreate, canEdit, canDelete }) => {
  const [form] = Form.useForm();
  const [exports, setExports] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const currentQuery = useRef([1, 100, {}]);

  const loadExports = useCallback(async (pageNumber = 1, pageSize = 100, filters = {}) => {
    if (!canList) return;
    currentQuery.current = [pageNumber, pageSize, filters];
    setLoading(true);
    try {
      const res = await api.get("/lead-exports", {
        params: {
          "pagination[page]": pageNumber,
          "pagination[pageSize]": pageSize,
          sort: "createdAt:desc",
          ...(filters.title ? { "filters[title][$containsi]": filters.title } : {}),
        },
      });
      setExports((res.data.data || []).map(({ id, attributes }) => ({ id, ...attributes })));
      setTotal(res.data.meta?.pagination?.total || 0);
    } catch (error) {
      message.error(error.response?.data?.error?.message || "خطا در دریافت خروجی‌های لید");
    } finally {
      setLoading(false);
    }
  }, [canList]);

  useEffect(() => { loadExports(); }, [loadExports]);

  const openEditor = (record = {}) => {
    form.resetFields();
    form.setFieldsValue({ ...record, lead_owner: getLeadOwnerValues(record.lead_owner ?? "1") });
    setEditing(record);
  };

  const save = async () => {
    let values;
    try { values = await form.validateFields(); } catch { return; }
    setSaving(true);
    const data = {
      title: values.title.trim(),
      from_date: values.from_date || null,
      to_date: values.to_date || null,
      lead_owner: values.lead_owner.join(","),
    };
    try {
      if (editing.id) await api.put(`/lead-exports/${editing.id}`, { data });
      else await api.post("/lead-exports", { data });
      message.success("خروجی لید ذخیره شد.");
      setEditing(null);
      await loadExports(...currentQuery.current);
    } catch (error) {
      message.error(error.response?.data?.error?.message || "خطا در ذخیره خروجی لید");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (record) => {
    try {
      await api.delete(`/lead-exports/${record.id}`);
      message.success("خروجی لید حذف شد.");
      await loadExports(...currentQuery.current);
    } catch (error) {
      message.error(error.response?.data?.error?.message || "خطا در حذف خروجی لید");
    }
  };

  const columns = [
    { title: "عنوان", dataIndex: "title", searchable: true },
    { title: "از تاریخ", dataIndex: "from_date", render: renderExportDate },
    { title: "تا تاریخ", dataIndex: "to_date", render: renderExportDate },
    { title: "مالک لید", dataIndex: "lead_owner", render: getLeadOwnerLabel },
    ...((canView || canEdit || canDelete) ? [{
      title: "عملیات",
      render: (_, record) => (
        <Space>
          {canView && <Link to={`/lead-exports/${record.id}`}>مشاهده</Link>}
          {canEdit && <Button onClick={() => openEditor(record)}>ویرایش</Button>}
          {canDelete && record.has_items === false && <Popconfirm title="خروجی لید حذف شود؟" okText="حذف" cancelText="انصراف" onConfirm={() => remove(record)}><Button danger>حذف</Button></Popconfirm>}
        </Space>
      ),
    }] : []),
  ];

  if (!canList) return <div>شما دسترسی به خروجی‌های لید ندارید.</div>;

  return (
    <>
      <DataTable
        tableKey="lead-exports"
        id="LeadExports"
        dataSource={exports}
        setDataSource={setExports}
        columns={columns}
        loading={loading}
        reload={loadExports}
        dynamicPagination
        filtersBeautify={(filters) => ({ title: filters?.title?.[0] })}
        recordsCount={total}
        recordCreatorDisabled
        header={{
          title: "خروجی‌های لید",
          avatar: { icon: <DownloadOutlined />, size: "large" },
          extra: canCreate ? <Button type="primary" onClick={() => openEditor()}>خروجی لید جدید</Button> : null,
        }}
      />
      <Modal
        title={editing?.id ? "ویرایش خروجی لید" : "خروجی لید جدید"}
        open={editing !== null}
        confirmLoading={saving}
        okText="ذخیره"
        cancelText="انصراف"
        onOk={save}
        onCancel={() => { if (!saving) setEditing(null); }}
        forceRender
      >
        <Form form={form} layout="vertical" disabled={saving}>
          <Form.Item name="title" label="عنوان" rules={[{ required: true, whitespace: true, message: "عنوان را وارد کنید." }, { max: 255 }]}>
            <Input maxLength={255} />
          </Form.Item>
          <Form.Item name="lead_owner" label="مالک لید" rules={[{ required: true, message: "مالک لید را انتخاب کنید." }]}>
            <Select mode="multiple" options={leadOwnerOptions} />
          </Form.Item>
          <Form.Item name="from_date" label="از تاریخ (اختیاری)"><ExportDatePicker /></Form.Item>
          <Form.Item name="to_date" label="تا تاریخ (اختیاری)" dependencies={["from_date"]} rules={[({ getFieldValue }) => ({
            validator: (_, value) => !value || !getFieldValue("from_date") || value >= getFieldValue("from_date")
              ? Promise.resolve() : Promise.reject(new Error("تا تاریخ باید برابر یا بعد از از تاریخ باشد.")),
          })]}><ExportDatePicker /></Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default connect((state) => {
  const permissions = state.userData?.permissions || [];
  return {
    canList: CheckPermission("lead_export_list", permissions),
    canView: CheckPermission("lead_export_get_details", permissions),
    canCreate: CheckPermission("lead_export_create", permissions),
    canEdit: CheckPermission("lead_export_edit", permissions),
    canDelete: CheckPermission("lead_export_delete", permissions),
  };
})(memo(LeadExports));
